import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { Divider, useAlert, useToast } from 'fasoo-ui-component-next';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import approvalDetailApi, {
  ApprovalDetailEntity,
  ApprovalStatusType,
  RequestApprovalDto,
} from 'src/api/user/approvalApi';
import { TakeOutApprovalDetailEntity } from 'src/api/user/takeOutApprovalApi';
import { CommonProps } from 'src/components/helpers/props';
import usePopup from 'src/hooks/usePopup';
import { getFasooUiComponentLanguage } from 'src/i18n';
import { toLocalDateTimeLangStr } from 'src/shared/utils/localDateTime';

import { ApprovalDetailFooter } from '../ApprovalDetailFooter';

import { ApprovalTypeIconArea } from './ApprovalTypeIconArea';
import RemarkPopup from './RemarkPopup';
import { ApprovalUserInfoDto, ShowLineContent, UserDetail } from '.';

import './PcTakeOutDetail.scss';

interface PcTakeOutDetailProps extends CommonProps {
  pcTakeOutApproval: TakeOutApprovalDetailEntity;
  ResetDetail: () => void;
  cancelApprovalChildren?: React.ReactNode;
  onCloseDetail: (open: boolean, isReject?: boolean) => void;
  isComplete?: boolean;
  selectedApproval?: ApprovalDetailEntity | null;
}

export const PcTakeOutDetail: React.FC<PcTakeOutDetailProps> = ({
  pcTakeOutApproval,
  cancelApprovalChildren,
  ResetDetail,
  onCloseDetail,
  isComplete = false,
  selectedApproval,
}) => {
  const { t } = useTranslation(['menu', 'userApproval']);
  const toast = useToast();
  const { alert } = useAlert();
  const openApprovalPopup = usePopup();
  const openRejectPopup = usePopup();
  const [approverList, setApproverList] = useState<ApprovalUserInfoDto[]>([]);

  let approvers: ApprovalUserInfoDto[] = [];

  if (pcTakeOutApproval.completeApprovers.length > 0) {
    const completes = _.sortBy(pcTakeOutApproval.completeApprovers, 'step');
    approvers = approvers.concat(
      completes.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: approver.approvalStatus,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
          actorIp: approver.actorIp,
          approvalRemark: approver.approvalRemark,
          approvalDate: approver.updatedTs,
          method: approver.approvalMethod,
          otherDeptCode: approver.otherDeptCode === null ? null : approver.otherDeptCode,
          otherDeptName: approver.otherDeptName === null ? null : approver.otherDeptName,
          otherPositionCode:
            approver.otherPositionCode === null ? null : approver.otherPositionCode,
          otherPositionName:
            approver.otherPositionName === null ? null : approver.otherPositionName,
          otherRoleCode: approver.otherRoleCode === null ? null : approver.otherRoleCode,
          otherRoleName: approver.otherRoleName === null ? null : approver.otherRoleName,
          otherUserId: approver.otherUserId === null ? null : approver.otherUserId,
          otherUserName: approver.otherUserName === null ? null : approver.otherUserName,
        };
      })
    );
  }
  if (pcTakeOutApproval.progressApprovers.length > 0) {
    const progresses = _.sortBy(pcTakeOutApproval.progressApprovers, 'step');
    approvers = approvers.concat(
      progresses.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
    );
  }
  // 결재선이 다 끝나지 않는 단계
  // if (pcTakeOutApproval.currentStep < pcTakeOutApproval.approvalStep) {
  // let postApprovalSteps: number[] = [];
  // for (let i = 1; i <= pcTakeOutApproval.approvalStep; i++) {
  //   postApprovalSteps = postApprovalSteps.concat(i);
  // }
  // console.log(postApprovalSteps);

  approvers = approvers.concat(
    pcTakeOutApproval.approvers
      .filter(
        (approver) =>
          // postApprovalSteps.includes(approver.step) &&
          approvers.filter((v) => v.step === approver.step)?.length === 0
      )
      .map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
  );

  const handleOpenApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.openPopup();
    } else if (type === 'reject') {
      openRejectPopup.openPopup();
    }
  };

  const handleCloseApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.closePopup();
    } else if (type === 'reject') {
      openRejectPopup.closePopup();
    }
  };

  const handleApprovalReject = (remark: string): void => {
    const popupType = openApprovalPopup.opened !== false ? 'approval' : 'reject';

    // console.log('얘가 작성한 remark : ', remark);
    if (pcTakeOutApproval.progressApprovers.length > 0) {
      const progressApproverInfo = pcTakeOutApproval.progressApprovers[0];
      const reqApprovalDto: RequestApprovalDto = {
        userId: progressApproverInfo.userId,
        approvalNo: pcTakeOutApproval.approvalNo,
        approvalMethod: pcTakeOutApproval.approvalMethod,
        approvalType: pcTakeOutApproval.approvalType,
        status: popupType === 'approval' ? 'APPROVAL' : 'REJECT',
        remark,
        proxyNo: selectedApproval?.progressApprovers[0].proxyNo
          ? selectedApproval.progressApprovers[0].proxyNo
          : null,
      };
      approvalDetailApi
        .approvalOrReject(reqApprovalDto)
        .then((resp) => {
          onCloseDetail(false, true);
        })
        .catch((err) => alert(err, 'error', '', undefined, true));
    } else {
      onCloseDetail(false);
    }

    handleCloseApprovalPopup(popupType);
  };

  return (
    <div className="pc-takeout-detail-root">
      <div
        className={classNames('pc-takeout-main-container', {
          'mg mb-85': isComplete === false,
          // 'mg mb-32': isComplete === true,
        })}
      >
        {/* 신청 유형 아이콘 area */}
        <ApprovalTypeIconArea
          onClose={onCloseDetail}
          type={pcTakeOutApproval.approvalType}
          status={pcTakeOutApproval.status}
        />

        <div className="detail-body">
          {/* 반출 기간 */}
          <ShowLineContent
            title={t('userApproval:period.$')}
            content={`${toLocalDateTimeLangStr(
              pcTakeOutApproval.startDate,
              getFasooUiComponentLanguage()
            )} ~ ${toLocalDateTimeLangStr(
              pcTakeOutApproval.endDate,
              getFasooUiComponentLanguage()
            )}`}
          />
          {/* 승인 방법  */}
          <ShowLineContent
            classes={classNames('approval-method')}
            title={t('userApproval:approvalMethod.$')}
            content={t(`userApproval:approvalMethod.${pcTakeOutApproval.approvalMethod}`)}
          />

          {/* 신청자 */}
          <ShowLineContent
            title={t('userApproval:applicant')}
            content={
              <UserDetail
                type="request"
                dateStr={toLocalDateTimeLangStr(
                  pcTakeOutApproval.applyDate,
                  getFasooUiComponentLanguage()
                )}
                remark={pcTakeOutApproval.remark}
                userInfo={{
                  userId: pcTakeOutApproval.userId,
                  userName: pcTakeOutApproval.userName,
                  positionName: pcTakeOutApproval.positionName,
                  deptName: pcTakeOutApproval.deptName,
                  actorIp: pcTakeOutApproval.actorIp,
                }}
              />
            }
          />
        </div>

        {/* 결재 상세 정보 */}
        <div className="approval-detail-info-title">
          <div className="title-text">{t('userApproval:approvalDetail.$')}</div>
        </div>
        <div className="detail-body">
          <ShowLineContent
            title=""
            content={
              <>
                {approvers.map((approver, idx) => {
                  return (
                    <UserDetail
                      step={approvers.length > 1 ? approver.step : undefined}
                      type="approver"
                      dateStr={
                        approver.approvalDate
                          ? toLocalDateTimeLangStr(
                              approver.approvalDate,
                              getFasooUiComponentLanguage()
                            )
                          : null
                      }
                      remark={approver.approvalRemark ? approver.approvalRemark : null}
                      userInfo={approver}
                      key={`approver-${idx}`}
                    />
                  );
                })}

                {approvers.length <= 1 || <Divider />}
              </>
            }
          />

          {cancelApprovalChildren !== undefined && cancelApprovalChildren}
        </div>
      </div>
      {isComplete === false && (
        <ApprovalDetailFooter
          approvalMethod={pcTakeOutApproval.approvalMethod}
          handleApprove={() => handleOpenApprovalPopup('approval')}
          handleReject={() => handleOpenApprovalPopup('reject')}
        />
      )}
      {(openApprovalPopup.opened === true || openRejectPopup.opened === true) && (
        <RemarkPopup
          open={
            openApprovalPopup.opened === true ? openApprovalPopup.opened : openRejectPopup.opened
          }
          entity={pcTakeOutApproval}
          onSubmit={handleApprovalReject}
          onClose={handleCloseApprovalPopup}
          isReject={openApprovalPopup.opened !== true}
        />
      )}
    </div>
  );
};

export default PcTakeOutDetail;
